import { memo } from "react";
import { ReactSVG } from "react-svg";
import { classNames, formatCurrency, useTranslate } from "helpers";
import { CHANNEL_CONFIG, CHANNEL_SORT_ORDER, DEFAULT_CHANNEL_CONFIG } from "services/api/sales/listings/consts";
import { VehicleListing, VehicleListingChannel } from "services/api/sales/listings/types";
import { Tooltip } from "ui/components/Tooltip";

interface ListingHeaderProps {
  listing: VehicleListing;
  channel: VehicleListingChannel;
}

const ListingHeader: React.FC<ListingHeaderProps> = ({ listing, channel }) => {
  const { t } = useTranslate();

  // Fallback chain za naslov: per-kanal override → listing baseTitle → ime
  // vozila → placeholder. Koristi `||` (a ne `??`) da prazni stringovi padnu
  // kroz fallback.
  const displayTitle = channel.effectiveTitle
    || channel.titleOverride
    || listing.baseTitle
    || listing.fleetVehicleName
    || "Bez naslova";

  // Cijena: per-kanal override → listing basePrice. `??` (ne `||`) jer je 0
  // legitimna vrijednost. Marketplace detail snapshot trenutno ne čuva cijenu
  // pa nema dodatnog fallbacka tamo.
  const displayPrice = channel.effectivePrice
    ?? channel.priceOverride
    ?? listing.basePrice;

  // Aktivni kanali za overlap stack u gornjem desnom kutu, sortirani istim
  // redoslijedom kao na pretrazi oglasa.
  const activeChannels = [...listing.channels]
    .filter((c) => c.isActive)
    .sort((a, b) =>
      (CHANNEL_SORT_ORDER[a.salesChannelCode ?? ""] ?? 99) -
      (CHANNEL_SORT_ORDER[b.salesChannelCode ?? ""] ?? 99)
    );

  // Provjera razlikuju li se cijene po kanalima — ako sve dijele istu
  // baseline cijenu, tooltip nema dodatne vrijednosti pa ga ne renderiramo.
  const prices = activeChannels.map((c) => c.effectivePrice ?? c.priceOverride ?? listing.basePrice);
  const allSame = prices.every((p) => p === prices[0]);
  const showTooltip = activeChannels.length > 0 && !allSame;

  const priceBlock = (
    <div>
      <div className="text-sm text-green-600 text-right">
        {t("admin.sales.listing.detail.price")}
      </div>
      <div className={classNames(
        "text-xl font-semibold text-green-600 truncate text-right",
        showTooltip ? "border-b border-dashed border-green-300 cursor-help" : ""
      )}>
        {displayPrice != null ? formatCurrency.format(displayPrice) : "-"}
      </div>
    </div>
  );

  return (
    <div className="border-b border-slate-200 pt-4 px-4">
      <div className="flex justify-between items-start mb-4">
        <div className="max-w-xl">
          <div className="text-md font-bold text-slate-600 truncate">
            {displayTitle}
          </div>
          <div className="text-sm text-slate-600">
            {listing.fleetVehicleName}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {activeChannels.length > 0 && (
            <div className="flex items-center -space-x-2">
              {activeChannels.map((ch) => {
                const cfg = CHANNEL_CONFIG[ch.salesChannelCode ?? ""] || DEFAULT_CHANNEL_CONFIG;
                return (
                  <span
                    key={ch.id}
                    title={ch.salesChannelName ?? ""}
                    className={classNames(
                      "w-8 h-8 rounded-full flex items-center justify-center border-2 border-white shadow-sm",
                      cfg.badge.bg,
                      cfg.badge.text
                    )}
                  >
                    <ReactSVG src={cfg.icon} className="w-4 h-4 [&_svg]:w-4 [&_svg]:h-4" />
                  </span>
                );
              })}
            </div>
          )}
          {showTooltip ? (
            <Tooltip position="top-right" size="large" trigger={priceBlock}>
              <div className="min-w-[260px]">
                <div className="text-xs uppercase text-slate-400 mb-3 font-semibold">
                  Cijena po oglasniku
                </div>
                <div className="space-y-2">
                  {activeChannels.map((ch) => {
                    const cfg = CHANNEL_CONFIG[ch.salesChannelCode ?? ""] || DEFAULT_CHANNEL_CONFIG;
                    const chPrice = ch.effectivePrice ?? ch.priceOverride ?? listing.basePrice;
                    return (
                      <div key={ch.id} className="flex justify-between items-center text-sm gap-3">
                        <span className="inline-flex items-center gap-2">
                          <span className={classNames(
                            "w-6 h-6 rounded-full flex items-center justify-center",
                            cfg.badge.bg,
                            cfg.badge.text
                          )}>
                            <ReactSVG src={cfg.icon} className="w-3 h-3 [&_svg]:w-3 [&_svg]:h-3" />
                          </span>
                          <span className="text-slate-200">{ch.salesChannelName}</span>
                        </span>
                        <span className="font-semibold text-white whitespace-nowrap">
                          {chPrice != null ? formatCurrency.format(chPrice) : "-"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Tooltip>
          ) : (
            priceBlock
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(ListingHeader);
